import React, { useCallback, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/atoms/checkbox'
import { useAppDispatch } from '@/features/hooks'
import { confirmCancellation } from '@/features/restaurant-manager/restaurant-manager-thunks'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { RestaurantCancellationHistoryGroup, RestaurantHighlightRules } from '@/models/restaurant-manager'
import { RestaurantCancellationCard } from './restaurant-cancellation-card'

type Props = {
  historyGroups: RestaurantCancellationHistoryGroup[] | null
  highlightRules: RestaurantHighlightRules | null
  isLoadingHistory: boolean
  hasHistoryError: boolean
  isOpen: boolean
  onClose: () => void
}

export const RestaurantCancellationHistoryPanel: React.FC<Props> = ({
  historyGroups,
  highlightRules,
  isLoadingHistory,
  hasHistoryError,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { errorHandler } = useErrorHandler()
  const [isConfirming, setIsConfirming] = useState(false)
  const [showPendingOnly, setShowPendingOnly] = useState(true)

  const onConfirmCancellation = useCallback(
    async (reservationId: string, salesDate: string, cancelledAt: string, isConfirmed: boolean) => {
      try {
        setIsConfirming(true)
        await dispatch(confirmCancellation({ reservationId, salesDate, cancelledAt, isConfirmed })).unwrap()
      } catch (error) {
        errorHandler(error)
      } finally {
        setIsConfirming(false)
      }
    },
    [dispatch, errorHandler],
  )

  const visibleGroups = useMemo(() => {
    return (historyGroups ?? [])
      .map(group => ({
        ...group,
        cancellations: showPendingOnly ? group.cancellations.filter(cancellation => !cancellation.isConfirmed) : group.cancellations,
      }))
      .filter(group => group.cancellations.length > 0)
  }, [historyGroups, showPendingOnly])

  const isLoading = isLoadingHistory || isConfirming

  if (!isOpen) {
    return null
  }

  return (
    <div css={overlayStyle} onClick={onClose}>
      <aside css={panelStyle} onClick={event => event.stopPropagation()}>
        <div css={headerStyle}>
          <button type="button" onClick={onClose} css={backButtonStyle} aria-label={t('Back')}>
            <img src={require('@/static/images/arrow_left_gray.svg')} alt="" css={backIconStyle} />
          </button>
          <div css={titleStyle}>{t('RestaurantManager.CancellationHistory')}</div>
        </div>

        <div css={toolbarStyle}>
          <Checkbox
            value={showPendingOnly}
            label={<span css={checkboxLabelStyle}>{t('RestaurantManager.ShowPendingOnly')}</span>}
            onChange={setShowPendingOnly}
          />
        </div>

        <div css={bodyStyle}>
          <CancellationBody
            isLoading={isLoading}
            hasError={hasHistoryError}
            visibleGroups={visibleGroups}
            isConfirming={isConfirming}
            onConfirm={onConfirmCancellation}
          />
        </div>
        <div css={footerStyle}>{t('RestaurantManager.TargetPeriodValue', { interval: highlightRules?.checkinDays ?? 3 })}</div>
      </aside>
    </div>
  )
}

type BodyProps = {
  isLoading: boolean
  hasError: boolean
  visibleGroups: RestaurantCancellationHistoryGroup[]
  isConfirming: boolean
  onConfirm: (reservationId: string, salesDate: string, cancelledAt: string, isConfirmed: boolean) => void
}

const CancellationBody: React.FC<BodyProps> = ({ isLoading, hasError, visibleGroups, isConfirming, onConfirm }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <div css={statusStyle}>{t('Loading')}</div>
  }

  if (hasError) {
    return <div css={statusStyle}>{t('Communication failed')}</div>
  }

  if (visibleGroups.length === 0) {
    return <div css={statusStyle}>{t('No data available')}</div>
  }

  return (
    <>
      {visibleGroups.map(group => (
        <section key={group.salesDate} css={groupStyle}>
          <div css={groupHeaderStyle}>
            <div css={groupDateStyle}>{dayjs(group.salesDate).format(t('YYYY-MM-DD'))}</div>
            <div css={groupCountStyle}>
              {group.cancellations.length}
              {t('item num')}
            </div>
          </div>

          <div css={cardListStyle}>
            {group.cancellations.map(cancellation => (
              <RestaurantCancellationCard
                key={`${cancellation.reservationId}-${cancellation.salesDate}`}
                cancellation={cancellation}
                isConfirming={isConfirming}
                onConfirm={onConfirm}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  )
}

const overlayStyle = css({
  background: 'transparent',
  bottom: 0,
  left: 0,
  pointerEvents: 'auto',
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: 1300,
})

const panelStyle = css({
  background: '#FFF',
  boxShadow: '0 0 6px rgba(0, 0, 0, 0.18), -2px 0 12px rgba(0, 0, 0, 0.08)',
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  right: 0,
  top: 0,
  width: 360,
})

const headerStyle = css({
  alignItems: 'center',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  gap: 6,
  minHeight: 76,
  background: '#F7F7F7',
})

const backButtonStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
})

const backIconStyle = css({
  width: 42,
})

const titleStyle = css({
  color: '#272727',
  fontSize: 17,
  fontWeight: 'bold',
  lineHeight: 1.3,
})

const toolbarStyle = css({
  borderBottom: '1px solid #F2F2F2',
  padding: '14px 18px',
})

const checkboxLabelStyle = css({
  color: '#676767',
  fontSize: 13,
  fontWeight: 'bold',
})

const bodyStyle = css({
  flex: 1,
  overflowY: 'auto',
  paddingBottom: 8,
})

const statusStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 14,
  justifyContent: 'center',
  minHeight: 140,
})

const groupStyle = css({
  display: 'flex',
  flexDirection: 'column',
})

const groupHeaderStyle = css({
  alignItems: 'center',
  background: '#F7F7F7',
  borderBottom: '1px solid #F0F0F0',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '12px 18px',
})

const groupDateStyle = css({
  color: '#676767',
  fontSize: 13,
  fontWeight: 'bold',
})

const groupCountStyle = css({
  color: '#676767',
  fontSize: 13,
  fontWeight: 'bold',
})

const cardListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '16px 18px',
})

const footerStyle = css({
  borderTop: '1px solid #F2F2F2',
  color: '#676767',
  fontSize: 11,
  lineHeight: 1.5,
  padding: '10px 18px 12px',
})
